import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import useSpeechToText from "../../hooks/useSpeechToText";
import { useTranslation } from "react-i18next";

interface VoiceInputProps {
  onResult: (text: string) => void;
  language: string;
  onStopRecording?: () => void;
}

const VoiceInput = forwardRef(
  ({ onResult, language, onStopRecording }: VoiceInputProps, ref) => {
    const { text, listening, start, stop } = useSpeechToText(language);
    const previousTextRef = useRef("");
    const [remainingTime, setRemainingTime] = useState(60);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const [microphoneError, setMicrophoneError] = useState<string>("");
    const [isTesting, setIsTesting] = useState(false);
    const hasTestedRef = useRef(false);
    const { t } = useTranslation();

    const isIphone =
      typeof navigator !== "undefined" &&
      /iPhone|iPad|iPod/i.test(navigator.userAgent);

    const getLanguageDisplay = (langCode: string) => {
      const languages: {
        [key: string]: {
          flag: string;
          name: string;
        };
      } = {
        "en-US": { flag: "🇺🇸", name: "EN" },
        "uz-UZ": {
          flag: "🇺🇿",
          name: "UZ",
        },
        "ko-KR": { flag: "🇰🇷", name: "KR" },
      };
      return (
        languages[langCode] || {
          flag: "🌐",
          name: "EN",
          record: "Record",
          stop: "Stop",
        }
      );
    };

    // Mikrofon test qilish
    const testMicrophone = async () => {
      setIsTesting(true);
      setMicrophoneError("");

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // Stream borligini tekshirdik, endi yopamiz
        stream.getTracks().forEach((track) => track.stop());

        // Test muvaffaqiyatli
        hasTestedRef.current = true;
        setMicrophoneError("");

        // Speech recognition boshlash
        start();
      } catch (error: any) {
        console.error("Microphone test error:", error);

        if (error.name === "NotAllowedError") {
          setMicrophoneError(
            "🚫 Mikrofon ruxsati berilmagan. Brauzer sozlamalaridan ruxsat bering."
          );
        } else if (error.name === "NotFoundError") {
          setMicrophoneError(
            "🎤 Mikrofon topilmadi. Qurilma ulanganligini tekshiring."
          );
        } else {
          setMicrophoneError("❌ Mikrofon xatosi: " + error.message);
        }
      } finally {
        setIsTesting(false);
      }
    };

    useEffect(() => {
      if (text && text !== previousTextRef.current) {
        onResult(text);
        previousTextRef.current = text;
      }
    }, [text, onResult]);

    useEffect(() => {
      if (listening) {
        setRemainingTime(60);
        timerRef.current = setInterval(() => {
          setRemainingTime((prev) => {
            if (prev <= 1) {
              handleStop();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setRemainingTime(60);
      }

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }, [listening]);

    const handleStop = () => {
      stop();
      if (onStopRecording) {
        onStopRecording();
      }
    };

    const handleStartRecording = async () => {
      if (isIphone) {
        alert(t("voice.unsupported"));
        return;
      }

      if (!listening) {
        if (!hasTestedRef.current) {
          await testMicrophone();
        } else {
          start();
        }
      } else {
        handleStop();
      }
    };

    useImperativeHandle(ref, () => ({
      stopRecording: () => {
        if (listening) {
          handleStop();
        }
      },
      isRecording: () => listening,
    }));

    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const langInfo = getLanguageDisplay(language);

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {/* Language badge */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 shadow-sm">
            <span className="text-base">{langInfo.flag}</span>
            <span className="text-xs font-semibold text-gray-700">
              {langInfo.name}
            </span>
          </div>

          {/* Recording button */}
          <button
            onClick={handleStartRecording}
            type="button"
            disabled={isTesting}
            className={`
              group relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
              transition-all duration-300 shadow-md hover:shadow-xl
              transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
              ${
                listening
                  ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                  : isTesting
                  ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-white"
                  : "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white"
              }
            `}
          >
            <div
              className={`absolute inset-0 rounded-lg blur-sm opacity-50 ${
                listening
                  ? "bg-red-400"
                  : isTesting
                  ? "bg-yellow-400"
                  : "bg-purple-400"
              }`}
            />

            <div className="relative flex items-center gap-2">
              {isTesting ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Testing...</span>
                </>
              ) : listening ? (
                <>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                  </span>
                  <span>{t("Stop")}</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 transition-transform group-hover:scale-110"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{t("Record")}</span>
                </>
              )}
            </div>
          </button>

          {/* Timer */}
          {listening && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200 shadow-sm">
              <svg
                className="w-3.5 h-3.5 text-red-600 animate-pulse"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs font-mono font-bold text-red-600 tabular-nums">
                {formatTime(remainingTime)}
              </span>
            </div>
          )}
        </div>

        {/* Error message */}
        {microphoneError && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">
              {microphoneError.split(" ")[0]}
            </span>
            <span className="flex-1">
              {microphoneError.split(" ").slice(1).join(" ")}
            </span>
          </div>
        )}
      </div>
    );
  }
);

VoiceInput.displayName = "VoiceInput";

export default VoiceInput;
